import React, { useState } from 'react';
import type { EnterpriseIndustryConfig } from '../../themes/themeTypes';
import { useRovingTabIndex } from '../../hooks/useRovingTabIndex';

interface IndustryTabsProps {
  industries?: EnterpriseIndustryConfig[];
  title?: string;
}

/**
 * B2B "Solutions by industry" tab panel used in Enterprise Gateway sections.
 * Shows industry list on the left, detailed description on the right.
 *
 * Implements the WAI-ARIA roving-tabindex tablist pattern: the active tab
 * carries tabindex=0, the rest tabindex=-1, and both arrow axes
 * (Up/Down per the declared vertical orientation, plus Left/Right for the
 * mobile row layout) + Home/End move the selection with focus following.
 * No visual change versus the previous click-only implementation.
 */
const IndustryTabs: React.FC<IndustryTabsProps> = ({
  industries,
  title = 'Solutions by industry',
}) => {
  const [activeIndex, setActiveIndex] = useState(0);

  const count = industries?.length ?? 0;
  // The tablist lays out horizontally on mobile (flex) and vertically on
  // desktop (md:flex-col) and declares aria-orientation="vertical", so it
  // accepts both arrow axes: Up/Down (honouring the declared orientation)
  // and Left/Right (matching the mobile row).
  const { onKeyDown, getTabIndex } = useRovingTabIndex(
    activeIndex,
    count,
    setActiveIndex,
    'both',
  );

  if (!industries || industries.length === 0) return null;

  const safeIndex = Math.min(activeIndex, industries.length - 1);
  const active = industries[safeIndex];

  return (
    <section
      className="bg-[var(--color-bg)]"
      aria-label={title}
      data-testid="industry-tabs"
    >
      <div
        className="container mx-auto px-lg py-3xl"
        style={{ maxWidth: 'var(--container-max-width)' }}
      >
        <h2 className="text-3xl md:text-4xl font-semibold text-primary mb-2xl text-center">
          {title}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-2xl">
          {/* Tabs */}
          <ul
            className="flex md:flex-col gap-sm list-none p-0 m-0 overflow-x-auto md:overflow-visible"
            role="tablist"
            aria-orientation="vertical"
          >
            {industries.map((industry, index) => {
              const isActive = index === safeIndex;
              return (
                <li key={industry.handle}>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    aria-controls={`industry-panel-${industry.handle}`}
                    id={`industry-tab-${industry.handle}`}
                    tabIndex={getTabIndex(index)}
                    onClick={() => setActiveIndex(index)}
                    onKeyDown={onKeyDown}
                    className={`
                      w-full px-lg py-md text-left rounded-sm text-sm font-medium transition-colors whitespace-nowrap
                      border
                      ${isActive
                        ? 'bg-primary text-[var(--color-text-on-primary)] border-primary'
                        : 'bg-transparent text-text-secondary border-border hover:text-primary hover:border-primary'
                      }
                      focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary
                    `}
                  >
                    {industry.label}
                  </button>
                </li>
              );
            })}
          </ul>

          {/* Panel */}
          <div
            role="tabpanel"
            id={`industry-panel-${active.handle}`}
            aria-labelledby={`industry-tab-${active.handle}`}
            // tabIndex=0 so keyboard users can reach the panel (WAI-ARIA tabs
            // pattern) — its content has no focusable children.
            tabIndex={0}
            className="bg-bg-secondary border border-border-light rounded-sm p-2xl focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            <h3 className="text-xl font-semibold text-primary mb-md">
              {active.label}
            </h3>
            {active.description && (
              <p className="text-text-secondary leading-relaxed">
                {active.description}
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default IndustryTabs;
